import { useState } from 'react'
import { useNavigate } from 'react-router'
import {
    PieChart,
    BarChart3,
    TrendingUp,
    Users,
    Target,
    Activity,
    ArrowRight,
    Calendar,
    Hash,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Filter,
    Search,
    RefreshCw
} from 'lucide-react'
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer 
} from 'recharts'
import { useDesilStats } from '../hooks/use-desil-stats'
import { useLatestDesil } from '../hooks/use-latest-desil'
import { useDesilByYear } from '../hooks/use-desil-by-year'
import { useDesilFiltered } from '../hooks/use-desil-filtered'
import { Spinner } from './ui/spinner'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { DesilFilter } from '../../../shared-lib/types'

interface DesilStatCardProps {
    title: string
    value: number
    icon: React.ReactNode
    color: 'emerald' | 'blue' | 'purple' | 'orange' | 'pink'
    subtitle: string
    onClick?: () => void
}

interface DesilDistributionChartProps {
    desilData: {
        desil1: number
        desil2: number
        desil3: number
        desil4: number
        desil5: number
        desil6_10: number
        belumDiperingkat: number
    }
    total: number
}

interface YearlyTrendChartProps {
    yearlyTrend: {
        tahun: number
        desil1: number
        desil2: number
        desil3: number
        desil4: number
        desil5: number
        desil6_10: number
        belumDiperingkat: number
        total: number
        jumlahKpm: number
    }[]
}

function DesilStatCard({ title, value, icon, color, subtitle, onClick }: DesilStatCardProps) {
    const colorClasses = {
        emerald: 'bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700',
        blue: 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
        purple: 'bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
        orange: 'bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
        pink: 'bg-gradient-to-br from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700'
    }

    return (
        <div
            className={`${colorClasses[color]} text-white p-6 rounded-xl shadow-lg ${onClick ? 'cursor-pointer' : ''} transition-all duration-300 transform hover:scale-105 hover:shadow-xl group`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between">
                <div className="text-white opacity-90 group-hover:opacity-100 transition-opacity">
                    {icon}
                </div>
                {onClick && <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />}
            </div>
            <div className="mt-4">
                <h3 className="text-sm font-medium opacity-90">{title}</h3>
                <p className="text-3xl font-bold mt-1">{value.toLocaleString('id-ID')}</p>
                <p className="text-sm opacity-80 mt-1">{subtitle}</p>
            </div>
        </div>
    )
}

function DesilDistributionChart({ desilData, total }: DesilDistributionChartProps) {
    const [selectedDesil, setSelectedDesil] = useState<string | null>(null)

    const desilItems = [
        { label: 'Desil 1', value: desilData.desil1, color: '#ef4444', description: 'Tingkat kesejahteraan terendah' },
        { label: 'Desil 2', value: desilData.desil2, color: '#f97316', description: 'Tingkat kesejahteraan rendah' },
        { label: 'Desil 3', value: desilData.desil3, color: '#f59e0b', description: 'Tingkat kesejahteraan menengah bawah' },
        { label: 'Desil 4', value: desilData.desil4, color: '#eab308', description: 'Tingkat kesejahteraan menengah' },
        { label: 'Desil 5', value: desilData.desil5, color: '#84cc16', description: 'Tingkat kesejahteraan menengah atas' },
        { label: 'Desil 6-10', value: desilData.desil6_10, color: '#22c55e', description: 'Tingkat kesejahteraan tinggi' },
        { label: 'Belum Diperingkat', value: desilData.belumDiperingkat, color: '#6b7280', description: 'Data belum tersedia' }
    ]

    const maxValue = Math.max(...desilItems.map(item => item.value))

    return (
        <Card className="border-emerald-200 shadow-lg">
            <CardHeader className="pb-4">
                <div className="flex items-center space-x-2">
                    <BarChart3 className="h-6 w-6 text-emerald-600" />
                    <CardTitle className="text-xl font-bold text-gray-900">Distribusi Desil Kesejahteraan</CardTitle>
                </div>
                <CardDescription>
                    Distribusi masyarakat berdasarkan klasifikasi desil kesejahteraan
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {desilItems.map((item, index) => {
                        const percentage = total > 0 ? (item.value / total) * 100 : 0
                        const width = maxValue > 0 ? (item.value / maxValue) * 100 : 0
                        const isSelected = selectedDesil === item.label

                        return (
                            <div
                                key={index}
                                className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${isSelected
                                    ? 'border-emerald-300 bg-emerald-50 shadow-md'
                                    : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                                    }`}
                                onClick={() => setSelectedDesil(isSelected ? null : item.label)}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-3">
                                        <div
                                            className="w-4 h-4 rounded"
                                            style={{ backgroundColor: item.color }}
                                        ></div>
                                        <span className="font-semibold text-gray-900">{item.label}</span>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-lg text-gray-900">
                                            {item.value.toLocaleString('id-ID')}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {percentage.toFixed(1)}%
                                        </div>
                                    </div>
                                </div>

                                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                                    <div
                                        className="h-3 rounded-full transition-all duration-500"
                                        style={{
                                            width: `${width}%`,
                                            backgroundColor: item.color
                                        }}
                                    ></div>
                                </div>

                                <div className="text-sm text-gray-600">
                                    {item.description}
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="text-center">
                        <h4 className="font-semibold text-emerald-800 mb-2">Total Keluarga Penerima Manfaat (KPM)</h4>
                        <p className="text-2xl font-bold text-emerald-700">{total.toLocaleString('id-ID')}</p>
                        <p className="text-sm text-emerald-600 mt-1">Total keseluruhan data desil kesejahteraan</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

function YearlyTrendChart({ yearlyTrend }: YearlyTrendChartProps) {
    const [selectedYear, setSelectedYear] = useState<number | null>(null)

    if (yearlyTrend.length === 0) {
        return (
            <Card className="border-blue-200 shadow-lg">
                <CardHeader>
                    <div className="flex items-center space-x-2">
                        <TrendingUp className="h-6 w-6 text-blue-600" />
                        <CardTitle className="text-xl font-bold text-gray-900">Tren Tahunan</CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8">
                        <p className="text-gray-500">Belum ada data tren tahunan tersedia</p>
                    </div>
                </CardContent>
            </Card>
        )
    }

    const maxTotal = Math.max(...yearlyTrend.map(item => item.total))

    return (
        <Card className="border-blue-200 shadow-lg">
            <CardHeader>
                <div className="flex items-center space-x-2">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                    <CardTitle className="text-xl font-bold text-gray-900">Tren Tahunan Desil Kesejahteraan</CardTitle>
                </div>
                <CardDescription>
                    Perkembangan distribusi desil kesejahteraan dari tahun ke tahun
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {yearlyTrend.map((item, index) => {
                        const height = maxTotal > 0 ? (item.total / maxTotal) * 100 : 0
                        const isSelected = selectedYear === item.tahun

                        return (
                            <div
                                key={index}
                                className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${isSelected
                                    ? 'border-blue-300 bg-blue-50 shadow-md'
                                    : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                                    }`}
                                onClick={() => setSelectedYear(isSelected ? null : item.tahun)}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center space-x-2">
                                        <Calendar className="h-5 w-5 text-blue-600" />
                                        <span className="font-semibold text-lg text-gray-900">Tahun {item.tahun}</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-3">
                                    <div className="text-center p-2 bg-red-50 rounded border border-red-200">
                                        <div className="text-xs text-red-700 font-medium">Desil 1</div>
                                        <div className="text-sm font-bold text-red-800">
                                            {item.desil1.toLocaleString('id-ID')}
                                        </div>
                                    </div>
                                    <div className="text-center p-2 bg-orange-50 rounded border border-orange-200">
                                        <div className="text-xs text-orange-700 font-medium">Desil 2</div>
                                        <div className="text-sm font-bold text-orange-800">
                                            {item.desil2.toLocaleString('id-ID')}
                                        </div>
                                    </div>
                                    <div className="text-center p-2 bg-yellow-50 rounded border border-yellow-200">
                                        <div className="text-xs text-yellow-700 font-medium">Desil 3</div>
                                        <div className="text-sm font-bold text-yellow-800">
                                            {item.desil3.toLocaleString('id-ID')}
                                        </div>
                                    </div>
                                    <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
                                        <div className="text-xs text-blue-700 font-medium">Desil 4</div>
                                        <div className="text-sm font-bold text-blue-800">
                                            {item.desil4.toLocaleString('id-ID')}
                                        </div>
                                    </div>
                                    <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                                        <div className="text-xs text-green-700 font-medium">Desil 5</div>
                                        <div className="text-sm font-bold text-green-800">
                                            {item.desil5.toLocaleString('id-ID')}
                                        </div>
                                    </div>
                                    <div className="text-center p-2 bg-emerald-50 rounded border border-emerald-200">
                                        <div className="text-xs text-emerald-700 font-medium">Desil 6-10</div>
                                        <div className="text-sm font-bold text-emerald-800">
                                            {item.desil6_10.toLocaleString('id-ID')}
                                        </div>
                                    </div>
                                    <div className="text-center p-2 bg-gray-50 rounded border border-gray-200">
                                        <div className="text-xs text-gray-700 font-medium">Belum Diperingkat</div>
                                        <div className="text-sm font-bold text-gray-800">
                                            {item.belumDiperingkat.toLocaleString('id-ID')}
                                        </div>
                                    </div>
                                </div>

                                <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                                    <div
                                        className="h-4 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                                        style={{ width: `${height}%` }}
                                    ></div>
                                </div>

                                {/* Total and KPM Stats Below Progress Bar */}
                                <div className="flex flex-col sm:flex-row justify-center gap-3 mt-3">
                                    {/* Total Container */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-center flex-1 max-w-[140px]">
                                        <div className="font-bold text-lg text-blue-700">
                                            {item.total.toLocaleString('id-ID')}
                                        </div>
                                        <div className="text-xs text-blue-600 font-medium">Total Keluarga</div>
                                    </div>
                                    {/* KPM Container */}
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 text-center flex-1 max-w-[140px]">
                                        <div className="font-bold text-lg text-emerald-700">
                                            {item.jumlahKpm.toLocaleString('id-ID')}
                                        </div>
                                        <div className="text-xs text-emerald-600 font-medium">Jumlah KPM</div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </CardContent>
        </Card>
    )
}

function YearlyDetailView() {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
    const [queryYear, setQueryYear] = useState(new Date().getFullYear())
    const { data: yearlyData, isLoading, error } = useDesilByYear(queryYear, true)

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const year = parseInt(e.target.value)
        if (!isNaN(year) && year >= 2020 && year <= 2030) {
            setSelectedYear(year)
        }
    }

    const handleGetData = () => {
        setQueryYear(selectedYear)
    }

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Data Desil per Tahun</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (error) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Data Desil per Tahun</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center text-red-500 p-8">
                        Terjadi kesalahan saat memuat data
                    </div>
                </CardContent>
            </Card>
        )
    }

    const getTahapColor = (tahap: number) => {
        const colors = [
            'bg-red-500 text-white',      // Tahap 1 - Sangat Miskin
            'bg-orange-500 text-white',   // Tahap 2 - Miskin
            'bg-yellow-500 text-white',   // Tahap 3 - Hampir Miskin
            'bg-blue-500 text-white',     // Tahap 4 - Rentan
            'bg-indigo-500 text-white',   // Tahap 5 - Cukup
            'bg-purple-500 text-white',   // Tahap 6 - Menengah Bawah
            'bg-pink-500 text-white',     // Tahap 7 - Menengah
            'bg-green-500 text-white',    // Tahap 8 - Menengah Atas
            'bg-teal-500 text-white',     // Tahap 9 - Kaya
            'bg-emerald-500 text-white'   // Tahap 10 - Sangat Kaya
        ]
        return colors[tahap - 1] || 'bg-gray-500 text-white'
    }

    const getTahapLabel = (tahap: number) => {
        const labels = [
            'Sangat Miskin',     // Tahap 1
            'Miskin',            // Tahap 2
            'Hampir Miskin',     // Tahap 3
            'Rentan',            // Tahap 4
            'Cukup',             // Tahap 5
            'Menengah Bawah',    // Tahap 6
            'Menengah',          // Tahap 7
            'Menengah Atas',     // Tahap 8
            'Kaya',              // Tahap 9
            'Sangat Kaya'        // Tahap 10
        ]
        return labels[tahap - 1] || `Tahap ${tahap}`
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-col space-y-4">
                    <CardTitle>Data Desil Tahun {queryYear}</CardTitle>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <label className="text-sm font-medium">Tahun:</label>
                            <Input
                                type="number"
                                value={selectedYear}
                                onChange={handleYearChange}
                                min={2020}
                                max={2030}
                                className="w-20"
                            />
                        </div>
                        <Button onClick={handleGetData} size="sm">
                            Ambil Data
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {!yearlyData?.data?.data || yearlyData.data.data.length === 0 ? (
                    <div className="text-center text-gray-500 p-8">
                        Tidak ada data desil untuk tahun {queryYear}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
                                <div className="text-sm text-blue-600 font-medium">Total KPM</div>
                                <div className="text-2xl font-bold text-blue-900">
                                    {yearlyData.data.data.reduce((sum: number, item: any) => sum + item.jumlahKpm, 0)}
                                </div>
                            </div>
                            <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg">
                                <div className="text-sm text-green-600 font-medium">Tahap Tertinggi</div>
                                <div className="text-2xl font-bold text-green-900">
                                    Tahap {Math.max(...yearlyData.data.data.map((item: any) => item.tahap))}
                                </div>
                            </div>
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg">
                                <div className="text-sm text-purple-600 font-medium">Total Data</div>
                                <div className="text-2xl font-bold text-purple-900">
                                    {yearlyData.data.data.length} Record{yearlyData.data.data.length !== 1 ? 's' : ''}
                                </div>
                            </div>
                        </div>

                        {/* Detailed Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {yearlyData.data.data
                                .sort((a: any, b: any) => a.tahap - b.tahap)
                                .map((item: any) => (
                                    <div
                                        key={`${item.tahap}-${item.id}`}
                                        className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTahapColor(item.tahap)}`}>
                                                Tahap {item.tahap}
                                            </span>
                                            <span className="text-sm text-gray-500">
                                                {item.tahun}
                                            </span>
                                        </div>

                                        <div className="space-y-3">
                                            {/* Basic Info */}
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <div className="text-xs text-gray-600">Jumlah KPM</div>
                                                    <div className="text-lg font-bold text-blue-600">
                                                        {item.jumlahKpm}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-600">Total KK</div>
                                                    <div className="text-lg font-bold text-green-600">
                                                        {item.total}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Desil Breakdown */}
                                            <div>
                                                <div className="text-xs text-gray-600 mb-2 font-medium">Distribusi Desil:</div>
                                                <div className="grid grid-cols-3 gap-1 text-xs">
                                                    <div className="bg-red-50 p-1 rounded">
                                                        <span className="text-red-700">Desil 1:</span>
                                                        <span className="font-medium ml-1">{item.desil1}</span>
                                                    </div>
                                                    <div className="bg-orange-50 p-1 rounded">
                                                        <span className="text-orange-700">Desil 2:</span>
                                                        <span className="font-medium ml-1">{item.desil2}</span>
                                                    </div>
                                                    <div className="bg-yellow-50 p-1 rounded">
                                                        <span className="text-yellow-700">Desil 3:</span>
                                                        <span className="font-medium ml-1">{item.desil3}</span>
                                                    </div>
                                                    <div className="bg-blue-50 p-1 rounded">
                                                        <span className="text-blue-700">Desil 4:</span>
                                                        <span className="font-medium ml-1">{item.desil4}</span>
                                                    </div>
                                                    <div className="bg-indigo-50 p-1 rounded">
                                                        <span className="text-indigo-700">Desil 5:</span>
                                                        <span className="font-medium ml-1">{item.desil5}</span>
                                                    </div>
                                                    <div className="bg-green-50 p-1 rounded">
                                                        <span className="text-green-700">Desil 6-10:</span>
                                                        <span className="font-medium ml-1">{item.desil6_10}</span>
                                                    </div>
                                                </div>
                                                <div className="mt-1">
                                                    <div className="bg-gray-50 p-1 rounded text-xs">
                                                        <span className="text-gray-700">Belum Diperingkat:</span>
                                                        <span className="font-medium ml-1">{item.belumDiperingkat}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Desa Info */}
                                            {item.desa && (
                                                <div className="pt-2 border-t border-gray-100">
                                                    <div className="text-xs text-gray-600">Desa</div>
                                                    <div className="text-sm font-medium">{item.desa.name}</div>
                                                    <div className="text-xs text-gray-500">Kode: {item.desa.code}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

// Helper function to prepare data for Line Chart
function prepareLineChartData(data: any[]) {
    // Group data by year and tahap, then create line chart points
    const sortedData = data.sort((a, b) => {
        if (a.tahun !== b.tahun) return a.tahun - b.tahun;
        return a.tahap - b.tahap;
    });

    return sortedData.map(item => ({
        name: `${item.tahun} - T${item.tahap}`,
        tahun: item.tahun,
        tahap: item.tahap,
        'Desil 1': item.desil1,
        'Desil 2': item.desil2,
        'Desil 3': item.desil3,
        'Desil 4': item.desil4,
        'Desil 5': item.desil5,
        'Desil 6-10': item.desil6_10,
        'Belum Diperingkat': item.belumDiperingkat,
        'Total KPM': item.jumlahKpm
    }));
}

function FilteredDataSummary() {
    const [filters, setFilters] = useState<DesilFilter>({
        sortOrder: 'desc'
    })
    const [appliedFilters, setAppliedFilters] = useState<DesilFilter>(filters)
    
    // State for desil category filter (for line chart display)
    const [selectedDesilCategories, setSelectedDesilCategories] = useState<string[]>([
        'Desil 1', 'Desil 2', 'Desil 3', 'Desil 4', 'Desil 5', 'Desil 6-10', 'Belum Diperingkat', 'Total KPM'
    ])

    const { data: filteredData, isLoading, error } = useDesilFiltered(appliedFilters)

    const handleFilterChange = (key: keyof DesilFilter, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value === '' ? undefined : value
        }))
    }

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters })
    }

    const handleResetFilters = () => {
        const resetFilters: DesilFilter = {
            sortOrder: 'desc'
        }
        setFilters(resetFilters)
        setAppliedFilters(resetFilters)
    }

    // Handler for desil category filter
    const toggleDesilCategory = (category: string) => {
        setSelectedDesilCategories(prev => {
            if (prev.includes(category)) {
                return prev.filter(c => c !== category)
            } else {
                return [...prev, category]
            }
        })
    }

    const selectAllCategories = () => {
        setSelectedDesilCategories(['Desil 1', 'Desil 2', 'Desil 3', 'Desil 4', 'Desil 5', 'Desil 6-10', 'Belum Diperingkat', 'Total KPM'])
    }

    const deselectAllCategories = () => {
        setSelectedDesilCategories([])
    }

    const currentYearForInputs = new Date().getFullYear()

    return (
        <Card className="border-blue-200 shadow-lg">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <Filter className="h-6 w-6 text-blue-600" />
                        <div>
                            <CardTitle className="text-xl font-bold text-gray-900">
                                Ringkasan Data Desil
                            </CardTitle>
                            <CardDescription>
                                Filter data berdasarkan tahap dan tahun (Default: Tahap 1, Semua Tahun)
                            </CardDescription>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {/* Filter Controls */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-blue-50 rounded-lg">
                    {/* Tahap Filter */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Tahap</label>
                        <select
                            value={filters.tahap || ''}
                            onChange={(e) => handleFilterChange('tahap', e.target.value ? Number(e.target.value) : undefined)}
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        >
                            <option value="">Semua Tahap</option>
                            <option value={1}>Tahap 1</option>
                            <option value={2}>Tahap 2</option>
                            <option value={3}>Tahap 3</option>
                            <option value={4}>Tahap 4</option>
                        </select>
                    </div>

                    {/* Start Year Filter */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Tahun Mulai</label>
                        <Input
                            type="number"
                            placeholder="2020"
                            min="2000"
                            max={currentYearForInputs}
                            value={filters.startYear || ''}
                            onChange={(e) => handleFilterChange('startYear', e.target.value ? Number(e.target.value) : undefined)}
                            className="text-sm"
                        />
                    </div>

                    {/* End Year Filter */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Tahun Selesai</label>
                        <Input
                            type="number"
                            placeholder={currentYearForInputs.toString()}
                            min="2000"
                            max={currentYearForInputs}
                            value={filters.endYear || ''}
                            onChange={(e) => handleFilterChange('endYear', e.target.value ? Number(e.target.value) : undefined)}
                            className="text-sm"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Aksi</label>
                        <div className="flex gap-2">
                            <Button
                                onClick={handleApplyFilters}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                                size="sm"
                                disabled={isLoading}
                            >
                                <Search className="h-4 w-4 mr-1" />
                                Terapkan
                            </Button>
                            <Button
                                onClick={handleResetFilters}
                                variant="outline"
                                size="sm"
                                className="px-3"
                            >
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Results Summary */}
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <Spinner className="h-6 w-6 mr-2" />
                        <span className="text-gray-600">Memuat data...</span>
                    </div>
                )}

                {error && (
                    <div className="text-center py-4 text-red-600">
                        <p>Terjadi kesalahan saat memuat data</p>
                    </div>
                )}

                {filteredData?.data && !isLoading && (
                    <div className="space-y-4">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg">
                                <div className="text-2xl font-bold">{filteredData.data.totals.jumlahKpm.toLocaleString('id-ID')}</div>
                                <div className="text-sm opacity-90">Total KPM</div>
                            </div>
                            <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-4 rounded-lg">
                                <div className="text-2xl font-bold">{filteredData.data.totals.desil1.toLocaleString('id-ID')}</div>
                                <div className="text-sm opacity-90">Desil 1</div>
                            </div>
                            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4 rounded-lg">
                                <div className="text-2xl font-bold">{filteredData.data.totals.desil6_10.toLocaleString('id-ID')}</div>
                                <div className="text-sm opacity-90">Desil 6-10</div>
                            </div>
                            <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white p-4 rounded-lg">
                                <div className="text-2xl font-bold">{filteredData.data.totals.belumDiperingkat.toLocaleString('id-ID')}</div>
                                <div className="text-sm opacity-90">Belum Diperingkat</div>
                            </div>
                        </div>

                        {/* Line Chart for Trend Analysis */}
                        {filteredData.data.data.length > 0 && (
                            <Card className="p-4">
                                <CardHeader>
                                    <CardTitle className="text-lg font-bold">Trend Desil Per Tahap dan Tahun</CardTitle>
                                    <CardDescription>
                                        Grafik garis menampilkan tren setiap kategori desil dari tahap ke tahap dan tahun ke tahun
                                    </CardDescription>
                                </CardHeader>
                                
                                {/* Desil Category Filter */}
                                <div className="px-6 pb-4 border-b border-gray-200">
                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                        <span className="text-sm font-medium text-gray-700">Tampilkan Kategori:</span>
                                        <Button 
                                            onClick={selectAllCategories}
                                            size="sm" 
                                            variant="outline"
                                            className="text-xs h-7"
                                        >
                                            Pilih Semua
                                        </Button>
                                        <Button 
                                            onClick={deselectAllCategories}
                                            size="sm" 
                                            variant="outline"
                                            className="text-xs h-7"
                                        >
                                            Hapus Semua
                                        </Button>
                                    </div>
                                    
                                    <div className="flex flex-wrap gap-2">
                                        {['Desil 1', 'Desil 2', 'Desil 3', 'Desil 4', 'Desil 5', 'Desil 6-10', 'Belum Diperingkat', 'Total KPM'].map(category => (
                                            <label key={category} className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDesilCategories.includes(category)}
                                                    onChange={() => toggleDesilCategory(category)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className={`text-sm ${
                                                    selectedDesilCategories.includes(category) 
                                                        ? 'text-gray-900 font-medium' 
                                                        : 'text-gray-500'
                                                }`}>
                                                    {category}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <CardContent>
                                    <div className="h-96 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart
                                                data={prepareLineChartData(filteredData.data.data)}
                                                margin={{
                                                    top: 20,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 20,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis 
                                                    dataKey="name" 
                                                    angle={-45}
                                                    textAnchor="end"
                                                    height={80}
                                                    interval={0}
                                                />
                                                <YAxis />
                                                <Tooltip 
                                                    labelStyle={{ color: '#374151' }}
                                                    contentStyle={{ 
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '6px'
                                                    }}
                                                />
                                                <Legend />
                                                {selectedDesilCategories.includes('Desil 1') && (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="Desil 1" 
                                                        stroke="#ef4444" 
                                                        strokeWidth={3}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                )}
                                                {selectedDesilCategories.includes('Desil 2') && (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="Desil 2" 
                                                        stroke="#f97316" 
                                                        strokeWidth={3}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                )}
                                                {selectedDesilCategories.includes('Desil 3') && (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="Desil 3" 
                                                        stroke="#eab308" 
                                                        strokeWidth={3}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                )}
                                                {selectedDesilCategories.includes('Desil 4') && (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="Desil 4" 
                                                        stroke="#22c55e" 
                                                        strokeWidth={3}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                )}
                                                {selectedDesilCategories.includes('Desil 5') && (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="Desil 5" 
                                                        stroke="#06b6d4" 
                                                        strokeWidth={3}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                )}
                                                {selectedDesilCategories.includes('Desil 6-10') && (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="Desil 6-10" 
                                                        stroke="#8b5cf6" 
                                                        strokeWidth={3}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                )}
                                                {selectedDesilCategories.includes('Belum Diperingkat') && (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="Belum Diperingkat" 
                                                        stroke="#64748b" 
                                                        strokeWidth={3}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                )}
                                                {selectedDesilCategories.includes('Total KPM') && (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="Total KPM" 
                                                        stroke="#1e40af" 
                                                        strokeWidth={4}
                                                        strokeDasharray="8 4"
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Complete Data Table */}
                        {/* {filteredData.data.data.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs border-collapse border border-gray-200">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="border border-gray-200 px-2 py-1 text-center">Tahun</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Tahap</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">KPM</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Desil 1</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Desil 2</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Desil 3</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Desil 4</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Desil 5</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Desil 6-10</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Belum Diperingkat</th>
                                            <th className="border border-gray-200 px-2 py-1 text-center">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.data.data.map((item, index) => (
                                            <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>

                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.tahun}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.tahap}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.jumlahKpm.toLocaleString('id-ID')}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.desil1.toLocaleString('id-ID')}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.desil2.toLocaleString('id-ID')}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.desil3.toLocaleString('id-ID')}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.desil4.toLocaleString('id-ID')}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.desil5.toLocaleString('id-ID')}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.desil6_10.toLocaleString('id-ID')}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center">
                                                    {item.belumDiperingkat.toLocaleString('id-ID')}
                                                </td>
                                                <td className="border border-gray-200 px-2 py-1 text-center font-medium">
                                                    {item.total.toLocaleString('id-ID')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )} */}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export function DesilStatisticsChart() {
    const navigate = useNavigate()
    const { data: statsData, isLoading: statsLoading, error: statsError } = useDesilStats()
    const { data: latestData, isLoading: latestLoading } = useLatestDesil()

    if (statsLoading || latestLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <Spinner className="h-8 w-8" />
                <p className="text-gray-600 mt-4">Memuat data desil kesejahteraan...</p>
            </div>
        )
    }

    if (statsError || !statsData || statsData.status === 'error') {
        return (
            <div className="text-center py-16">
                <div className="text-red-500 mb-4">
                    <BarChart3 className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-semibold mb-2">Data Tidak Tersedia</h3>
                    <p className="text-gray-600 mb-6">
                        {statsError?.message || 'Gagal memuat data desil kesejahteraan'}
                    </p>
                </div>
                <Button onClick={() => window.location.reload()} variant="outline">
                    Coba Lagi
                </Button>
            </div>
        )
    }

    const stats = statsData.data
    const latestDesil = latestData?.data

    // Check for empty data state
    if (!stats || stats.totalRecords === 0) {
        return (
            <div className="text-center py-20">
                <div className="mb-8">
                    <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-full flex items-center justify-center mx-auto mb-6">
                        <PieChart className="h-12 w-12 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        Belum Ada Data Desil Kesejahteraan
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto mb-8 leading-relaxed">
                        Data desil kesejahteraan belum tersedia. Sistem siap untuk menampilkan analisis mendalam
                        tingkat kesejahteraan masyarakat ketika data sudah diinput.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
                    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100 shadow-lg">
                        <CardContent className="p-6 text-center">
                            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <BarChart3 className="h-6 w-6 text-white" />
                            </div>
                            <h4 className="font-semibold text-emerald-800 mb-2">Analisis Distribusi</h4>
                            <p className="text-sm text-emerald-700">
                                Visualisasi distribusi tingkat kesejahteraan berdasarkan klasifikasi desil
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg">
                        <CardContent className="p-6 text-center">
                            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <TrendingUp className="h-6 w-6 text-white" />
                            </div>
                            <h4 className="font-semibold text-blue-800 mb-2">Tren Tahunan</h4>
                            <p className="text-sm text-blue-700">
                                Grafik perkembangan data kesejahteraan dari tahun ke tahun
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 shadow-lg">
                        <CardContent className="p-6 text-center">
                            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Target className="h-6 w-6 text-white" />
                            </div>
                            <h4 className="font-semibold text-purple-800 mb-2">Statistik Komprehensif</h4>
                            <p className="text-sm text-purple-700">
                                Ringkasan statistik dan data terbaru untuk insight mendalam
                            </p>
                        </CardContent>
                    </Card>
                </div>

                <div className="flex flex-wrap gap-4 justify-center">
                    <Button
                        className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl px-6 py-3 shadow-lg font-semibold"
                        onClick={() => navigate('/')}
                    >
                        <ArrowRight className="h-5 w-5 mr-2" />
                        Kembali ke Beranda
                    </Button>
                    <Button
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl px-6 py-3 shadow-lg font-semibold"
                        onClick={() => navigate('/statistics')}
                    >
                        <BarChart3 className="h-5 w-5 mr-2" />
                        Lihat Statistik Lainnya
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-8">
            {/* Header Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <DesilStatCard
                    title="Total Data"
                    value={stats.totalRecords}
                    icon={<Hash className="h-8 w-8" />}
                    color="emerald"
                    subtitle="Total pencatatan desil"
                />
                <DesilStatCard
                    title="Total KPM"
                    value={stats.totalKpm}
                    icon={<Users className="h-8 w-8" />}
                    color="blue"
                    subtitle="Keluarga Penerima Manfaat"
                />
                <DesilStatCard
                    title="Tahap Terbaru"
                    value={stats.latestTahap || 0}
                    icon={<Target className="h-8 w-8" />}
                    color="purple"
                    subtitle="Tahap pencatatan terakhir"
                />
                <DesilStatCard
                    title="Tahun Terbaru"
                    value={stats.latestTahun || 0}
                    icon={<Calendar className="h-8 w-8" />}
                    color="orange"
                    subtitle="Periode data terbaru"
                />
            </div>

            {/* Latest Desil Info */}
            {latestDesil && (
                <Card className="border-indigo-200 shadow-lg">
                    <CardHeader>
                        <div className="flex items-center space-x-2">
                            <Activity className="h-6 w-6 text-indigo-600" />
                            <CardTitle className="text-xl font-bold text-gray-900">Data Desil Terbaru</CardTitle>
                        </div>
                        <CardDescription>
                            Tahap {latestDesil.tahap} - Tahun {latestDesil.tahun}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                            <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                                <div className="text-sm font-medium text-red-700">Desil 1</div>
                                <div className="text-xl font-bold text-red-800">{latestDesil.desil1}</div>
                            </div>
                            <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                                <div className="text-sm font-medium text-orange-700">Desil 2</div>
                                <div className="text-xl font-bold text-orange-800">{latestDesil.desil2}</div>
                            </div>
                            <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div className="text-sm font-medium text-yellow-700">Desil 3</div>
                                <div className="text-xl font-bold text-yellow-800">{latestDesil.desil3}</div>
                            </div>
                            <div className="text-center p-3 bg-lime-50 rounded-lg border border-lime-200">
                                <div className="text-sm font-medium text-lime-700">Desil 4</div>
                                <div className="text-xl font-bold text-lime-800">{latestDesil.desil4}</div>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                                <div className="text-sm font-medium text-green-700">Desil 5</div>
                                <div className="text-xl font-bold text-green-800">{latestDesil.desil5}</div>
                            </div>
                            <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                <div className="text-sm font-medium text-emerald-700">Desil 6-10</div>
                                <div className="text-xl font-bold text-emerald-800">{latestDesil.desil6_10}</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="text-sm font-medium text-gray-700">Belum Peringkat</div>
                                <div className="text-xl font-bold text-gray-800">{latestDesil.belumDiperingkat}</div>
                            </div>
                            <div className="text-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                                <div className="text-sm font-medium text-indigo-700">Total</div>
                                <div className="text-xl font-bold text-indigo-800">{latestDesil.total}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <DesilDistributionChart
                    desilData={{
                        desil1: stats.totalDesil1,
                        desil2: stats.totalDesil2,
                        desil3: stats.totalDesil3,
                        desil4: stats.totalDesil4,
                        desil5: stats.totalDesil5,
                        desil6_10: stats.totalDesil6_10,
                        belumDiperingkat: stats.totalBelumDiperingkat
                    }}
                    total={stats.grandTotal}
                />

                <YearlyTrendChart yearlyTrend={stats.yearlyTrend} />
            </div>

            {/* Summary Stats */}
            <Card className="border-gray-200 shadow-lg">
                <CardHeader>
                    <CardTitle className="text-xl font-bold text-gray-900">Ringkasan Statistik</CardTitle>
                    <CardDescription>
                        Rata-rata dan total keseluruhan data desil kesejahteraan
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                        <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                            <div className="text-sm font-medium text-red-700 mb-1">Rata-rata Desil 1</div>
                            <div className="text-2xl font-bold text-red-800">{stats.avgDesil1}</div>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                            <div className="text-sm font-medium text-orange-700 mb-1">Rata-rata Desil 2</div>
                            <div className="text-2xl font-bold text-orange-800">{stats.avgDesil2}</div>
                        </div>
                        <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <div className="text-sm font-medium text-yellow-700 mb-1">Rata-rata Desil 3</div>
                            <div className="text-2xl font-bold text-yellow-800">{stats.avgDesil3}</div>
                        </div>
                        <div className="text-center p-4 bg-lime-50 rounded-lg border border-lime-200">
                            <div className="text-sm font-medium text-lime-700 mb-1">Rata-rata Desil 4</div>
                            <div className="text-2xl font-bold text-lime-800">{stats.avgDesil4}</div>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="text-sm font-medium text-green-700 mb-1">Rata-rata Desil 5</div>
                            <div className="text-2xl font-bold text-green-800">{stats.avgDesil5}</div>
                        </div>
                        <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                            <div className="text-sm font-medium text-emerald-700 mb-1">Rata-rata Desil 6-10</div>
                            <div className="text-2xl font-bold text-emerald-800">{stats.avgDesil6_10}</div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="text-sm font-medium text-gray-700 mb-1">Rata-rata Belum Peringkat</div>
                            <div className="text-2xl font-bold text-gray-800">{stats.avgBelumDiperingkat}</div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Filtered Data Summary */}
            <FilteredDataSummary />

            {/* Yearly Detail View */}
            <YearlyDetailView />
        </div>
    )
}